import { FormEvent, ReactNode, useState } from "react";
import {
  createReportId,
  FEEDBACK_ATTEMPT_STORAGE_KEY,
  FEEDBACK_DRAFT_STORAGE_KEY,
  parseEvidenceLinks,
  parseFeedbackResponse,
  toFeedbackRequest,
  type FeedbackAttempt,
  type FeedbackDraft,
} from "@/lib/beta-feedback";

const fieldClass = "w-full border border-ink bg-paper px-3 py-2 font-sans normal-case tracking-normal outline-none focus:outline-2 focus:outline-offset-2 focus:outline-spot";

const EMPTY_DRAFT: FeedbackDraft = {
  kind: "bug",
  title: "",
  message: "",
  steps: "",
  expected: "",
  area: "",
  severity: "major",
  contact: "",
  attachmentLinks: [],
};
const read = <T,>(key: string) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};
const write = (key: string, value: unknown): boolean => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};
const remove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {}
};
const draftAtLoad = () =>
  typeof window === "undefined"
    ? EMPTY_DRAFT
    : { ...EMPTY_DRAFT, ...(read<Partial<FeedbackDraft>>(FEEDBACK_DRAFT_STORAGE_KEY) ?? {}) };
const attemptAtLoad = () =>
  typeof window === "undefined" ? null : read<FeedbackAttempt>(FEEDBACK_ATTEMPT_STORAGE_KEY);

export function FeedbackForm() {
  const [draft, setDraft] = useState<FeedbackDraft>(draftAtLoad);
  const [attempt, setAttempt] = useState<FeedbackAttempt | null>(attemptAtLoad);
  const [reportId, setReportId] = useState(() => attemptAtLoad()?.request?.reportId ?? createReportId());
  const [evidenceText, setEvidenceText] = useState(() => draftAtLoad().attachmentLinks?.join("\n") ?? "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [storageWarning, setStorageWarning] = useState(false);
  const [receipt, setReceipt] = useState<{
    reportId: string;
    status: "pending" | "submitted";
    issueUrl?: string;
  } | null>(null);
  const evidence = parseEvidenceLinks(evidenceText);
  const update = <K extends keyof FeedbackDraft>(key: K, value: FeedbackDraft[K]) => {
    const next = { ...draft, [key]: value };
    setDraft(next);
    setError("");
    setStorageWarning(!write(FEEDBACK_DRAFT_STORAGE_KEY, next));
  };
  const updateEvidence = (value: string) => {
    const next = { ...draft, attachmentLinks: parseEvidenceLinks(value).links };
    setEvidenceText(value);
    setDraft(next);
    setError("");
    setStorageWarning(!write(FEEDBACK_DRAFT_STORAGE_KEY, next));
  };
  const startNew = () => {
    remove(FEEDBACK_ATTEMPT_STORAGE_KEY);
    remove(FEEDBACK_DRAFT_STORAGE_KEY);
    setStorageWarning(false);
    setAttempt(null);
    setReceipt(null);
    setError("");
    setDraft(EMPTY_DRAFT);
    setEvidenceText("");
    setReportId(createReportId());
  };

  async function send(frozen: FeedbackAttempt) {
    setLoading(true);
    setError("");
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 15_000);
    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(frozen.request),
        signal: controller.signal,
      });
      const payload: unknown = await response.json().catch(() => null);
      if (response.status >= 400 && response.status < 500) {
        remove(FEEDBACK_ATTEMPT_STORAGE_KEY);
        setAttempt(null);
        setReportId(createReportId());
        setError((payload as { error?: string } | null)?.error || "Please correct the report and send it again.");
        return;
      }
      const parsed = parseFeedbackResponse(payload, frozen.request.reportId);
      if (!response.ok || !parsed) throw new Error("Unknown delivery state");
      remove(FEEDBACK_ATTEMPT_STORAGE_KEY);
      remove(FEEDBACK_DRAFT_STORAGE_KEY);
      setAttempt(null);
      setReceipt({ reportId: parsed.data.reportId, status: parsed.data.status, issueUrl: parsed.data.issueUrl });
    } catch (cause) {
      setError(
        cause instanceof Error && cause.name === "AbortError"
          ? "The request timed out. Retry the exact saved report."
          : "The submission status is unknown. Retry the exact saved report."
      );
    } finally {
      window.clearTimeout(timer);
      setLoading(false);
    }
  }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (evidence.error) return setError(evidence.error);
    const built = toFeedbackRequest({ ...draft, attachmentLinks: evidence.links }, reportId);
    if (!built.request) return setError(built.error ?? "Check the report and try again.");
    const frozen = { request: built.request };
    setStorageWarning(!write(FEEDBACK_ATTEMPT_STORAGE_KEY, frozen));
    setAttempt(frozen);
    void send(frozen);
  }
  if (receipt) return <Receipt receipt={receipt} onNew={startNew} />;
  if (attempt)
    return (
      <Pending attempt={attempt} error={error} loading={loading} storageWarning={storageWarning} onRetry={() => void send(attempt)} onNew={startNew} />
    );
  return (
    <section className="landing-shell py-16 sm:py-24">
      <div className="grid gap-12 border-t border-ink pt-8 lg:grid-cols-[.8fr_1.2fr]">
        <header>
          <p className="font-mono text-xs uppercase tracking-[.16em] text-spot">Beta feedback</p>
          <h1 className="mt-4 font-display text-5xl leading-[.92] sm:text-6xl">Report a bug.</h1>
          <p className="mt-6 max-w-sm text-lg">Tell us what interrupted your work. Contact details are optional.</p>
          <p className="mt-4 max-w-sm font-mono text-xs leading-5">
            Drafts stay on this device. Never include passwords, API keys, or private client material.
          </p>
        </header>
        <form className="grid gap-6" onSubmit={submit}>
          <fieldset className="grid gap-2">
            <legend className="font-mono text-xs uppercase tracking-[.12em]">Type</legend>
            <div className="flex gap-2">
              <Choice active={draft.kind === "bug"} onClick={() => update("kind", "bug")}>
                Bug
              </Choice>
              <Choice active={draft.kind === "feedback"} onClick={() => update("kind", "feedback")}>
                Feedback
              </Choice>
            </div>
          </fieldset>
          <Field label="Title">
            <input
              required
              className={fieldClass}
              value={draft.title}
              onChange={e => update("title", e.target.value)}
              placeholder="A short summary"
              maxLength={160}
            />
          </Field>
          <Field label="What happened">
            <textarea
              required
              className={`${fieldClass} min-h-40`}
              value={draft.message}
              onChange={e => update("message", e.target.value)}
              placeholder="What were you trying to do, and what did Genie do?"
              maxLength={5000}
            />
          </Field>
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Steps to reproduce">
              <textarea
                className={`${fieldClass} min-h-28`}
                value={draft.steps}
                onChange={e => update("steps", e.target.value)}
                maxLength={5000}
              />
            </Field>
            <Field label="Expected result">
              <textarea
                className={`${fieldClass} min-h-28`}
                value={draft.expected}
                onChange={e => update("expected", e.target.value)}
                maxLength={2000}
              />
            </Field>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Area">
              <input
                className={fieldClass}
                value={draft.area}
                onChange={e => update("area", e.target.value)}
                placeholder="Flow, chat, desktop…"
                maxLength={80}
              />
            </Field>
            <Field label="Impact">
              <select
                className={fieldClass}
                value={draft.severity}
                onChange={e => update("severity", e.target.value as FeedbackDraft["severity"])}
              >
                <option value="blocking">Blocking</option>
                <option value="major">Major</option>
                <option value="minor">Minor</option>
              </select>
            </Field>
          </div>
          <Field label="Private contact">
            <input
              type="email"
              className={fieldClass}
              value={draft.contact}
              onChange={e => update("contact", e.target.value)}
              placeholder="you@example.com (optional)"
              maxLength={320}
            />
          </Field>
          <Field label="Evidence links">
            <textarea
              className={`${fieldClass} min-h-28`}
              value={evidenceText}
              onChange={e => updateEvidence(e.target.value)}
              placeholder={"https://…\nUp to five HTTPS links"}
            />
            <span className="font-sans text-xs normal-case tracking-normal">
              Optional. Link to screenshots or recordings you control; uploads are not available here.
            </span>
          </Field>
          {storageWarning && (
            <p role="status" className="border-l-2 border-spot pl-3 text-sm">
              Local storage is unavailable. Keep this tab open until you receive a receipt.
            </p>
          )}
          {error && (
            <p role="alert" className="border-l-2 border-spot pl-3 text-sm">
              {error}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-4">
            <button
              className="border border-ink bg-ink px-5 py-3 font-mono text-xs uppercase tracking-[.12em] text-paper"
              type="submit"
            >
              Send report
            </button>
            <span className="font-mono text-[11px] uppercase tracking-[.1em]">No GitHub account needed</span>
          </div>
        </form>
      </div>
    </section>
  );
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-2 font-mono text-xs uppercase tracking-[.12em]">
      {label}
      {children}
    </label>
  );
}
function Choice({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`border px-4 py-2 font-mono text-xs uppercase tracking-[.12em] ${active ? "border-ink bg-ink text-paper" : "border-ink"}`}
    >
      {children}
    </button>
  );
}
function Pending({
  attempt,
  error,
  loading,
  storageWarning,
  onRetry,
  onNew,
}: {
  attempt: FeedbackAttempt;
  error: string;
  loading: boolean;
  storageWarning: boolean;
  onRetry: () => void;
  onNew: () => void;
}) {
  return (
    <section className="landing-shell py-24">
      <div className="max-w-2xl border border-ink p-8 sm:p-12" aria-live="polite">
        <p className="font-mono text-xs uppercase tracking-[.16em] text-spot">Saved report</p>
        <h1 className="mt-4 font-display text-5xl leading-none">Retry this exact report.</h1>
        <p className="mt-6">The delivery result is unknown, so the report is frozen to prevent duplicates.</p>
        <p className="mt-3 font-mono text-xs">Report ID: {attempt.request.reportId}</p>
        {storageWarning && <p role="status" className="mt-4 border-l-2 border-spot pl-3 text-sm">Local storage is unavailable. Keep this tab open until you receive a receipt.</p>}
        {error && (
          <p role="alert" className="mt-4 border-l-2 border-spot pl-3 text-sm">
            {error}
          </p>
        )}
        <div className="mt-8 flex gap-4">
          <button
            disabled={loading}
            onClick={onRetry}
            className="border border-ink bg-ink px-5 py-3 font-mono text-xs uppercase tracking-[.12em] text-paper disabled:opacity-60"
          >
            {loading ? "Sending…" : "Retry report"}
          </button>
          <button disabled={loading} onClick={onNew} className="border border-ink px-5 py-3 font-mono text-xs uppercase tracking-[.12em] disabled:opacity-60">
            Start new report
          </button>
        </div>
      </div>
    </section>
  );
}
function Receipt({
  receipt,
  onNew,
}: {
  receipt: { reportId: string; status: "pending" | "submitted"; issueUrl?: string };
  onNew: () => void;
}) {
  return (
    <section className="landing-shell py-24">
      <div className="max-w-2xl border border-ink p-8 sm:p-12" aria-live="polite">
        <p className="font-mono text-xs uppercase tracking-[.16em] text-spot">Report {receipt.status}</p>
        <h1 className="mt-4 font-display text-5xl leading-none">
          {receipt.status === "submitted" ? "Thank you." : "Your report is safely queued."}
        </h1>
        <p className="mt-6">
          {receipt.status === "submitted"
            ? "The Genie team has received your beta report."
            : "We saved your report and will submit it as soon as the reporting service is available."}
        </p>
        <p className="mt-4 font-mono text-xs">Report ID: {receipt.reportId}</p>
        {receipt.issueUrl && (
          <>
            <a
              className="mt-8 inline-flex border border-ink px-4 py-2 font-mono text-xs uppercase tracking-[.12em] no-underline hover:bg-ink hover:text-paper"
              href={receipt.issueUrl}
              target="_blank"
              rel="noreferrer"
            >
              View report
            </a>
            <p className="mt-3 text-xs">This private issue link is available only to people with repository access.</p>
          </>
        )}
        <button className="mt-8 block font-mono text-xs uppercase tracking-[.12em] underline" onClick={onNew}>
          Send another report
        </button>
      </div>
    </section>
  );
}
